import { useMutation, useQueryClient } from '@tanstack/react-query';

import { questionnaireDesignApi } from '../api/questionnaire-design.adapter';
import { questionnaireDesignQueryKeys } from './use-item-questions';
import type {
  CopyQuestionInput,
  CreateQuestionInput,
  Question,
  UpdateQuestionInput,
} from '../types/questionnaire-design.types';

// Invalidate the per-item list (and the cross-item "all" cache used by the
// tree filter) so the new card appears without a manual reload. Failures
// surface as `mutation.error` — the caller is responsible for toast/UX.

function invalidateForItem(qc: ReturnType<typeof useQueryClient>, itemId: string) {
  qc.invalidateQueries({ queryKey: questionnaireDesignQueryKeys.questionsForItem(itemId) });
  qc.invalidateQueries({ queryKey: questionnaireDesignQueryKeys.allQuestions() });
}

export function useCreateQuestionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateQuestionInput) =>
      questionnaireDesignApi.createQuestion(input),
    onSuccess: (created: Question) => invalidateForItem(qc, created.itemId),
  });
}

export function useUpdateQuestionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ qid, input }: { qid: string; input: UpdateQuestionInput }) =>
      questionnaireDesignApi.updateQuestion(qid, input),
    onSuccess: (updated: Question) => invalidateForItem(qc, updated.itemId),
  });
}

export function useDeleteQuestionMutation() {
  const qc = useQueryClient();
  return useMutation({
    // The caller passes the current itemId alongside the qid so we can
    // invalidate immediately even though the response is `void`.
    mutationFn: ({ qid }: { qid: string; itemId: string }) =>
      questionnaireDesignApi.deleteQuestion(qid),
    onSuccess: (_void, vars) => invalidateForItem(qc, vars.itemId),
  });
}

export function useCopyQuestionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      qid,
      input,
    }: {
      qid: string;
      input: CopyQuestionInput;
    }) => questionnaireDesignApi.copyQuestion(qid, input),
    onSuccess: (copied: Question) => invalidateForItem(qc, copied.itemId),
  });
}
